import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Heading1Text,
  Heading2Text,
  TitleMedium,
} from '../../../../components/Texts';
import Button from '../../../../components/Button';
import WidthContainer from '../../../../components/WidthContainer';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import useCustomLogin from '../../../../hooks/useCustomLogin';
import ColorizedSpan from '../../../../components/Colorized';
import { useNavigate, useSearchParams } from 'react-router-dom';

const InnerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  margin: 10px 0px;
`;

const BackgroundColor = styled.div`
  background-color: ${({ theme }) => theme.primary.background};
  padding: 40px 0px;
  position: absolute;
  width: 100%;
  left: 0px;
`;

const Container = styled.div`
  min-height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    text-align: center;
    justify-content: center;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: row;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: row;
    gap: 10px;
  }
`;

const GetStarted = () => {
  const { t } = useTranslation();
  const backgroundRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);
  const [searchParams] = useSearchParams();
  const lpd = searchParams.get('lpd');
  const navigate = useNavigate();
  const { login } = useCustomLogin();

  const getHeading = () => {
    if (lpd === 'payments')
      return <>{t('landing.getStarted.headingPayments')}</>;
    if (lpd === 'yield')
      return (
        <>
          {t('landing.getStarted.headingYieldBefore')}
          <ColorizedSpan>
            {t('landing.getStarted.headingYieldHighlight')}
          </ColorizedSpan>
        </>
      );
    return (
      <>
        {t('landing.getStarted.headingDefaultBefore')}
        <ColorizedSpan>
          {t('landing.getStarted.headingDefaultHighlight')}
        </ColorizedSpan>
      </>
    );
  };

  const getSubtext = () => {
    if (lpd === 'payments') {
      return <TitleMedium>{t('landing.getStarted.subPayments')}</TitleMedium>;
    }
    if (lpd === 'yield') {
      return <TitleMedium>{t('landing.getStarted.subYield')}</TitleMedium>;
    }
    return (
      <>
        <TitleMedium>
          <b>{t('landing.getStarted.subDefaultIntro')}</b>
        </TitleMedium>
        <TitleMedium>{t('landing.getStarted.subDefaultSend')}</TitleMedium>
      </>
    );
  };

  const getButtonLabel = () => {
    if (lpd === 'payments') return t('landing.getStarted.buttonPayments');
    if (lpd === 'yield') return t('landing.getStarted.buttonYield');
    return t('landing.getStarted.buttonDefault');
  };

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  return (
    <>
      <BackgroundColor ref={backgroundRef}>
        <WidthContainer>
          <Container>
            <div
              style={{
                display: 'flex',
                gap: 24,
                flexDirection: 'column',
                width: '100%',
                marginBottom: isMobile ? '25px' : '0px',
              }}
            >
              <HeaderContainer>
                <Heading2Text
                  style={{
                    color: theme.text.title,
                    width: '100%',
                  }}
                >
                  {getHeading()}
                </Heading2Text>
              </HeaderContainer>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                {getSubtext()}
              </div>
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  if (lpd === 'payments') {
                    login();
                    return;
                  }
                  if (lpd.startsWith('checkout')) {
                    navigate(`/checkout/${lpd.split('-')[1]}`);
                    return;
                  }
                  if (lpd.startsWith('stripe-checkout')) {
                    navigate(
                      `/stripe-checkout/${lpd.split('stripe-checkout-')[1]}`,
                    );
                    return;
                  }
                  const params = new URLSearchParams();
                  if (lpd) params.set('lpd', lpd);
                  if (lpd === 'yield') {
                    params.set(
                      'tokenAddress',
                      'AvZZF1YaZDziPY2RCK4oJrRVrbN3mTD9NL24hPeaZeUj',
                    );
                    params.set('network', 'solana');
                  }
                  const query = params.toString();
                  navigate(
                    query ? `/app/gifts/create?${query}` : '/app/gifts/create',
                  );
                }}
              >
                {getButtonLabel()}
              </Button>
            </div>
            <InnerContainer>
              <img
                style={{ width: isMobile ? '100%' : '450px' }}
                src='/landing-v2/merry-christmas.webp'
              />
            </InnerContainer>
          </Container>
        </WidthContainer>
      </BackgroundColor>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default GetStarted;
